import { RequestStatus, useApi } from "@core-ui/hooks/useApi";
import SyncService from "@ext/git/actions/Sync/logic/SyncService";
import { PublishHealthcheckCode, type PublishHealthcheckResult } from "@ext/git/core/GitPublish/PublishHealthcheck";
import { PUBLISH_PANEL_ID } from "@ext/git/core/GitPublish/PublishPanel/constants";
import { useFloatingPanelStore } from "@ui-kit/FloatingPanel";
import { useEffect, useRef } from "react";

export type PublishHealth = {
	/** The request itself: publish waits for a running one so the two fetches do not overlap. */
	status: RequestStatus;
	healthcheckPending: boolean;
	isProtectedBranch: boolean;
	hasGitConflicts: boolean;
};

const usePublishHealth = (canPush: boolean): PublishHealth => {
	const { data, status, call } = useApi<PublishHealthcheckResult>({
		url: (api) => api.getStoragePublishHealthcheckUrl(),
	});

	const checkRef = useRef(call);
	checkRef.current = call;
	const conflictShownRef = useRef(false);
	conflictShownRef.current = data?.code === PublishHealthcheckCode.HasGitConflicts;

	// The panel keeps its content mounted after it closes, so a check on mount ran on the first open
	// only: a conflict a colleague pushed while the panel was closed never showed on reopening it.
	const isOpen = useFloatingPanelStore((state) => state.panels[PUBLISH_PANEL_ID]?.isOpen ?? false);
	useEffect(() => {
		if (isOpen) void checkRef.current();
	}, [isOpen]);

	// The mount-time check above already covers a panel that opens with storage already connected;
	// this is only for storage connecting while the panel stays open, so it must skip that first run.
	const wasCanPushRef = useRef(canPush);
	useEffect(() => {
		if (canPush && !wasCanPushRef.current) void checkRef.current();
		wasCanPushRef.current = canPush;
	}, [canPush]);

	useEffect(() => {
		// A conflict is settled outside this panel — resolved or aborted in the merge window, or gone by
		// the time the footer's sync runs — and only the healthcheck knows the outcome: without asking
		// again the footer keeps the button it had.
		const ask = () => void checkRef.current();
		const resolved = SyncService.events.on("conflict-resolved", ask);
		const aborted = SyncService.events.on("conflict-aborted", ask);
		// A sync that runs into the conflict ends with "finish" too, while the merge window is open and
		// the merge half done — asked then, the healthcheck answers "ok". Only a clean sync settles it.
		const finished = SyncService.events.on("finish", ({ syncData }) => {
			if (syncData.mergeData.ok && conflictShownRef.current) ask();
		});
		return () => {
			SyncService.events.off(resolved);
			SyncService.events.off(aborted);
			SyncService.events.off(finished);
		};
	}, []);

	// Pending only until the first answer: a re-check keeps the footer on the last one instead of
	// flashing a disabled «Publish» over a standing conflict on every open.
	const healthcheckPending = status === RequestStatus.Init || (status === RequestStatus.Loading && !data);

	return {
		status,
		healthcheckPending,
		isProtectedBranch: !healthcheckPending && data?.code === PublishHealthcheckCode.ProtectedBranch,
		hasGitConflicts: !healthcheckPending && data?.code === PublishHealthcheckCode.HasGitConflicts,
	};
};

export default usePublishHealth;
